import * as React from 'react';
import { t } from '@lingui/core/macro';
import DialogShell from 'components/DialogShell';

export interface IConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  'data-testid'?: string;
  /** Unique ID for alert filtering (e.g. 'DIALOG_END_SESSION_NON_ORGANIZER'). */
  dialogId?: string;
  /**
   * Optional custom content to render below the message.
   * Use this to add form fields, additional information, or any custom UI.
   */
  children?: React.ReactNode;
  /** When true, hides the cancel button — useful for informational dialogs with a single "OK" action. */
  hideCancel?: boolean;
  /** Lowercase custom-element tag name of the web component that should claim rendering (e.g. 'e3d-stream'); other hosts defer even if visible. */
  preferredClaimant?: string;
}

/**
 * A reusable confirmation dialog component that replaces window.confirm.
 * Provides a consistent modal interface for user confirmation actions.
 */
export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = t`Confirm`,
  cancelText = t`Cancel`,
  onConfirm,
  onCancel,
  'data-testid': dataTestId,
  children = null,
  hideCancel = false,
}: IConfirmDialogProps) {
  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = React.useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  // Handle Escape key
  React.useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleCancel]);

  if (!isOpen) return null;

  return (
    <DialogShell
      rootProps={{
        'aria-labelledby': 'modal-title',
        role: 'dialog',
        'aria-modal': true,
      }}
      rootTestId={dataTestId}
    >
      <div>
        <div className="mt-3 sm:mt-2">
          <h2
            part="dialog-title"
            className="text-base font-semibold"
            id="modal-title"
            data-testid="confirm-dialog-heading"
          >
            {title}
          </h2>
          {message && (
            <div className="mt-2">
              <p part="dialog-message" className="text-sm text-gray-700">
                {message}
              </p>
            </div>
          )}
          {children && (
            <div part="dialog-content" className="mt-4">
              {children}
            </div>
          )}
        </div>
      </div>
      <div className="mt-5 sm:mt-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          {!hideCancel && (
            <button
              part="dialog-cancel"
              type="button"
              onClick={handleCancel}
              className="hover:bg-gray-50 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal sm:w-auto"
              style={{
                backgroundColor: 'var(--ex3d-dialog-bg-color, var(--ex3d-dealer-white))',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'var(--ex3d-dialog-cancel-border-color, var(--ex3d-dealer-gray-300))',
                color: 'var(--ex3d-dialog-text-color, var(--ex3d-dealer-gray-700))',
              }}
              data-testid={dataTestId ? `${dataTestId}-cancel` : undefined}
            >
              {cancelText}
            </button>
          )}
          <button
            part="dialog-confirm"
            type="button"
            onClick={handleConfirm}
            className="inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal sm:w-auto"
            style={{
              backgroundColor: 'var(--ex3d-dialog-confirm-bg-color, var(--ex3d-dealer-primary))',
              color: 'var(--ex3d-dialog-confirm-text-color, var(--ex3d-dealer-white))',
            }}
            data-testid={dataTestId ? `${dataTestId}-confirm` : undefined}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </DialogShell>
  );
}
